"use client";

import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { NextLink } from "@/_components/ui/NextLink";
import { useLocale, useTranslations } from "next-intl";
import { useState, useRef, useCallback } from "react";
import { FiChevronDown } from "react-icons/fi";
import { IconType } from "react-icons";
import {
  HiOutlineUser,
  HiOutlineUserGroup,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";
import {
  TbFileDescription,
  TbShieldCheck,
  TbCreditCardRefund,
  TbBrain,
  TbDatabaseSearch,
  TbUsersGroup,
  TbReportAnalytics,
  TbLayoutDashboard,
  TbLock,
} from "react-icons/tb";
import { SiSpeedtest } from "react-icons/si";
interface SubNavItem {
  icon: IconType;
  title: string;
  description: string;
  href: string;
  external?: boolean;
}

interface SubNavGroup {
  label: string;
  items: SubNavItem[];
  image?: string;
}

interface SubNavConfig {
  groups: SubNavGroup[];
  width?: string;
}

const subNavLinkStyles = {
  fontFamily: "body" as const,
  pos: "relative" as const,
  _after: {
    content: '""',
    position: "absolute" as const,
    bottom: "-4px",
    left: 0,
    width: "full",
    height: "2px",
    backgroundColor: "primary.500",
    transform: "scaleX(0)",
    transition: "transform 0.2s ease-in-out",
  },
  _hover: {
    color: "neutral.900",
    _after: {
      transform: "scaleX(1)",
    },
  },
};

function useSubNavData(): Record<string, SubNavConfig> {
  const locale = useLocale();
  const t = useTranslations("landing.subnav");

  return {
    products: {
      width: "1000px",
      groups: [
        {
          label: t("products.core.label"),
          items: [
            {
              icon: TbBrain,
              title: t("products.core.screening.title"),
              description: t("products.core.screening.description"),
              href: "#products",
            },
            {
              icon: TbDatabaseSearch,
              title: t("products.core.talent_pool.title"),
              description: t("products.core.talent_pool.description"),
              href: "#products",
            },
            {
              icon: TbReportAnalytics,
              title: t("products.core.jd_management.title"),
              description: t("products.core.jd_management.description"),
              href: "#products",
            },
          ],
        },
        {
          label: t("products.more.label"),
          items: [
            {
              icon: TbLayoutDashboard,
              title: t("products.more.dashboard.title"),
              description: t("products.more.dashboard.description"),
              href: "#products",
            },
            {
              icon: TbUsersGroup,
              title: t("products.more.community.title"),
              description: t("products.more.community.description"),
              href: "#products",
            },
            {
              icon: TbLock,
              title: t("products.more.security.title"),
              description: t("products.more.security.description"),
              href: "#products",
            },
          ],
        },
        {
          label: t("products.tools.label"),
          items: [
            {
              icon: SiSpeedtest,
              title: t("products.tools.resume_benchmark.title"),
              description: t("products.tools.resume_benchmark.description"),
              href: "/resume-benchmark",
            },
          ],
        },
      ],
    },
    about: {
      width: "560px",
      groups: [
        {
          label: t("about.company.label"),
          items: [
            {
              icon: HiOutlineUserGroup,
              title: t("about.company.story.title"),
              description: t("about.company.story.description"),
              href: "#about",
            },
            {
              icon: HiOutlineUser,
              title: t("about.company.vision.title"),
              description: t("about.company.vision.description"),
              href: "#about",
            },
            {
              icon: HiOutlineOfficeBuilding,
              title: t("about.company.roadmap.title"),
              description: t("about.company.roadmap.description"),
              href: "#about",
            },
          ],
        },
        {
          label: t("about.legal.label"),
          items: [
            {
              icon: TbFileDescription,
              title: t("about.legal.terms.title"),
              description: t("about.legal.terms.description"),
              href: `/${locale}/help-center/terms-of-service`,
            },
            {
              icon: TbShieldCheck,
              title: t("about.legal.privacy.title"),
              description: t("about.legal.privacy.description"),
              href: `/${locale}/help-center/privacy-policy`,
            },
            {
              icon: TbCreditCardRefund,
              title: t("about.legal.refund.title"),
              description: t("about.legal.refund.description"),
              href: `/${locale}/help-center/refund-policy`,
            },
          ],
        },
      ],
    },
  };
}

export { useSubNavData };
export type { SubNavItem, SubNavGroup, SubNavConfig };

interface SubNavDropdownProps {
  navKey: string;
  label: string;
  color?: string;
  onScrollToSection?: (sectionId: string) => void;
}

export function SubNavDropdown({
  navKey,
  label,
  color = "neutral.500",
  onScrollToSection,
}: SubNavDropdownProps) {
  const [open, setOpen] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const subNavData = useSubNavData();
  const config = subNavData[navKey];

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);

  if (!config) return null;

  return (
    <Box
      position="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <HStack
        gap={0.5}
        cursor="pointer"
        transition="color 0.2s"
        onClick={() => setOpen((prev) => !prev)}
        {...subNavLinkStyles}
      >
        <Text
          textStyle="sm"
          color={open ? "neutral.900" : color}
          fontWeight="medium"
          transition="color 0.2s"
          userSelect="none"
        >
          {label}
        </Text>
        <Box
          display="inline-flex"
          transition="transform 0.25s cubic-bezier(.4,1.5,.5,1)"
          transform={open ? "rotate(180deg)" : "rotate(0deg)"}
        >
          <Icon
            as={FiChevronDown}
            boxSize={3.5}
            color={open ? "neutral.900" : color}
          />
        </Box>
      </HStack>

      <Box
        position="absolute"
        top="100%"
        left="50%"
        transform="translateX(-50%)"
        pt={3}
        opacity={open ? 1 : 0}
        visibility={open ? "visible" : "hidden"}
        transition="all 0.2s ease"
        zIndex={1400}
        pointerEvents={open ? "auto" : "none"}
      >
        <Box
          bg="white"
          borderRadius="16px"
          boxShadow="0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)"
          border="1px solid"
          borderColor="blackAlpha.100"
          minW={config.width || "560px"}
          overflow="hidden"
        >
          <Grid
            templateColumns={`repeat(${config.groups.length}, 1fr)`}
            gap={0}
          >
            {config.groups.map((group, groupIdx) => (
              <GridItem
                key={group.label}
                p={6}
                borderRight={
                  groupIdx < config.groups.length - 1 ? "1px solid" : undefined
                }
                borderColor="blackAlpha.100"
              >
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="primary.500"
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  mb={4}
                >
                  {group.label}
                </Text>
                <VStack align="stretch" gap={1}>
                  {group.items.map((item) => (
                    <SubNavItemCard
                      key={item.title}
                      item={item}
                      onClose={() => setOpen(false)}
                      onScrollToSection={onScrollToSection}
                    />
                  ))}
                </VStack>
              </GridItem>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

function SubNavItemCard({
  item,
  onClose,
  onScrollToSection,
}: {
  item: SubNavItem;
  onClose: () => void;
  onScrollToSection?: (sectionId: string) => void;
}) {
  const isAnchor = item.href.startsWith("#");

  const handleClick = (e: React.MouseEvent) => {
    if (isAnchor && onScrollToSection) {
      e.preventDefault();
      onScrollToSection(item.href.replace("#", ""));
      onClose();
    } else {
      onClose();
    }
  };

  return (
    <NextLink
      href={item.href}
      onClick={handleClick}
      target={item.external ? "_blank" : undefined}
      rel={item.external ? "noopener noreferrer" : undefined}
      _hover={{ textDecoration: "none" }}
    >
      <HStack
        gap={3}
        p={2.5}
        borderRadius="10px"
        transition="all 0.15s ease"
        _hover={{ bg: "neutral.50" }}
        align="start"
      >
        <Flex
          w="36px"
          h="36px"
          borderRadius="8px"
          bg="primary.50"
          align="center"
          justify="center"
          flexShrink={0}
          mt={0.5}
        >
          <Icon as={item.icon} boxSize={4.5} color="primary.500" />
        </Flex>
        <Box>
          <Text
            textStyle="sm"
            fontWeight="500"
            color="neutral.800"
            lineHeight="1.3"
            mb={0.5}
          >
            {item.title}
          </Text>
          <Text textStyle="xs" color="neutral.500" lineHeight="1.4" mt={1}>
            {item.description}
          </Text>
        </Box>
      </HStack>
    </NextLink>
  );
}

interface SubNavMobileAccordionProps {
  navKey: string;
  label: string;
  onClose: () => void;
  onScrollToSection?: (sectionId: string) => void;
}

export function SubNavMobileAccordion({
  navKey,
  label,
  onClose,
  onScrollToSection,
}: SubNavMobileAccordionProps) {
  const [expanded, setExpanded] = useState(false);
  const subNavData = useSubNavData();
  const config = subNavData[navKey];

  if (!config) return null;

  return (
    <Box w="full">
      <HStack
        gap={1}
        cursor="pointer"
        onClick={() => setExpanded((prev) => !prev)}
        py={1}
      >
        <Text
          fontWeight="light"
          fontFamily="heading"
          textStyle="xl"
          color="neutral.700"
        >
          {label}
        </Text>
        <Box
          display="inline-flex"
          transition="transform 0.25s cubic-bezier(.4,1.5,.5,1)"
          transform={expanded ? "rotate(180deg)" : "rotate(0deg)"}
        >
          <Icon as={FiChevronDown} boxSize={4} color="neutral.500" />
        </Box>
      </HStack>

      <Box
        overflow="hidden"
        maxH={expanded ? "1000px" : "0"}
        opacity={expanded ? 1 : 0}
        transition="all 0.3s ease"
      >
        <VStack align="stretch" gap={1} pl={2} pt={2} pb={3}>
          {config.groups.map((group) => (
            <Box key={group.label}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="primary.500"
                textTransform="uppercase"
                letterSpacing="0.08em"
                mb={2}
                mt={2}
              >
                {group.label}
              </Text>
              {group.items.map((item) => (
                <SubNavItemCard
                  key={item.title}
                  item={item}
                  onClose={onClose}
                  onScrollToSection={onScrollToSection}
                />
              ))}
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}
